/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
    hj(event: "event", event_name: string): void;
    hj(
      event: "identify",
      user_id: string,
      user_properties: Record<string, any>,
    ): void;
    hsConversationsSettings: any;
    HubSpotConversations: any;
    __pinia?: Record<string, any>;
    posthog?: {
      apiKey: string;
      host: string;
    };
  }
}

export type IsoDate = `${number}-${number}-${number}`;

export const FeatureFlag = {
  PROMO_CODES: "promo_codes",
  STANDING_ROOM: "standing_room",
  ENFORCE_MINIMUMS: "enforce_minimums",
  FEE_DISCLOSURE: "fee_disclosure",
  TERMINAL: "terminal",
  SKIP_SEATING_TIME: "skip_seating_time",
  LIVESTREAM: "livestream",
  CONTINUOUS_SERVICE: "continuous_service",
  PERFORMANCE: "performance",
  RESIDENCY_LANDING_V2: "residency_landing_v2",
} as const;
export type FeatureFlagType = (typeof FeatureFlag)[keyof typeof FeatureFlag];

export const ReservationStatus = {
  UNPAID: "unpaid",
  EXPECTED: "expected",
  ARRIVED: "arrived",
  SEATED: "seated",
  NO_SHOW: "no show",
  CANCELED: "canceled",
  CLEARED: "cleared",
  OBSOLETE: "obsolete",
  PAY_LATER: "paylater",
  FAILED: "failed",
  PARTIALLY_SEATED: "partially_seated",
  TABLE_READY: "table_ready",
} as const;
export type ReservationStatusType =
  (typeof ReservationStatus)[keyof typeof ReservationStatus];

export const UserGroup = {
  MANAGERS: "managers",
  OWNERS: "owners",
  REFUNDERS: "refunders",
} as const;
export type UserGroupType = (typeof UserGroup)[keyof typeof UserGroup];

export const TableShape = {
  CIRCLE: "circle",
  SQUARE: "square",
  TRIANGLE: "triangle",
  HALF_CIRCLE: "half-circle",
  PIANO: "piano",
} as const;
export type TableShapeType = (typeof TableShape)[keyof typeof TableShape];

export const EmailStatus = {
  NEW: "new",
  GOOD: "good",
  SPAM: "spam",
  BOUNCED: "bounced",
} as const;
export type EmailStatusType = (typeof EmailStatus)[keyof typeof EmailStatus];

export type ReportStatusType =
  (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];
export const REPORT_STATUS = {
  PENDING: "pending",
  WORKING: "working",
  COMPLETE: "complete",
  FAILED: "failed",
};

export type AvailabilityState =
  | "sold_out"
  | "limited"
  | "selling_fast"
  | "available";

export type BoolParam = "true" | "false";
export interface PerformanceQueryParams {
  page?: number;
  page_size?: number;
  start_date?: string;
  end_date?: string;
  date?: string;
  show?: number;
  pagination?: BoolParam;
  booking?: BoolParam;
}

export interface GuestExportArgs {
  accepts_marketing?: BoolParam;
  no_accepts_marketing?: BoolParam;
}

export interface ReservationExportArgs {
  start_date?: IsoDate;
  end_date?: IsoDate;
  show_start_date?: IsoDate;
  show_end_date?: IsoDate;
  residency_id?: number;
  performances?: number[];
}

export interface SalesReportArgs {
  lookback_days?: number;
}

export interface PerformanceReportArgs {
  performance_ids?: number[];
}

export type ReportArgs =
  | GuestExportArgs
  | ReservationExportArgs
  | SalesReportArgs
  | PerformanceReportArgs;

export type StripeLocationId = `tml_${string}`;
export type StripeReaderId = `tmr_${string}`;
export type StripeReaderStatus = "offline" | "online";
export type StripeReaderOutcome = "succeeded" | "failed" | "in_progress";

interface Flavoring<FlavorT> {
  _type?: FlavorT;
}
export type TypedId<FlavorT> = number & Flavoring<FlavorT>;

export interface SuccessResponse {
  success: true;
}

export interface ShowAttendanceRules {
  type: "show_attendance";
  show_ids: number[];
  recency_days: number;
}

export interface GuestTagRules {
  type: "guest_tag";
  tag_ids: number[];
}

export type GuestSegmentRules = ShowAttendanceRules | GuestTagRules;
// end of static_types.ts
export type AddOnId = TypedId<'AddOn'>
export type CouponId = TypedId<'Coupon'>
export type CSAreaId = TypedId<'CSArea'>
export type CSDateId = TypedId<'CSDate'>
export type CSDateTableId = TypedId<'CSDateTable'>
export type CSReservationId = TypedId<'CSReservation'>
export type CustomerId = TypedId<'Customer'>
export type FloorPlanId = TypedId<'FloorPlan'>
export type GuestSegmentId = TypedId<'GuestSegment'>
export type GuestTagId = TypedId<'GuestTag'>
export type InviteCodeId = TypedId<'InviteCode'>
export type OrderPricingId = TypedId<'OrderPricing'>
export type PerformanceId = TypedId<'Performance'>
export type PerformanceAddOnId = TypedId<'PerformanceAddOn'>
export type PerformanceSeatingTimeId = TypedId<'PerformanceSeatingTime'>
export type PerformanceTableId = TypedId<'PerformanceTable'>
export type PromotionCodeId = TypedId<'PromotionCode'>
export type ReportInstanceId = TypedId<'ReportInstance'>
export type ReportScheduleId = TypedId<'ReportSchedule'>
export type ReservationId = TypedId<'Reservation'>
export type ReservationAddOnId = TypedId<'ReservationAddOn'>
export type ReservationStatusId = TypedId<'ReservationStatus'>
export type ReservationTagId = TypedId<'ReservationTag'>
export type ResidencyId = TypedId<'Residency'>
export type ResidencyExternalLinkId = TypedId<'ResidencyExternalLink'>
export type RoomLayoutId = TypedId<'RoomLayout'>
export type SeatingTimeId = TypedId<'SeatingTime'>
export type ShowId = TypedId<'Show'>
export type ShowTimeId = TypedId<'ShowTime'>
export type TableId = TypedId<'Table'>
export type TableTypeId = TypedId<'TableType'>
export type TransactionId = TypedId<'Transaction'>
export type UserId = TypedId<'User'>
export interface AccountingReportRead {
    month: string;
    face: number;
    add_ons: number;
    discounts: number;
    refunds: number;
    service_fee: number;
    ticket_tax: number;
    add_ons_tax: number;
    stripe_fees: number;
    application_fees: number;
    guests_sum: number;
}

export interface AddOnRead {
    id: AddOnId;
    name: string;
    price: number;
    description: string;
    tax_rate: number;
}

export interface AddOnWrite {
    id?: AddOnId;
    name?: string;
    price: number;
    description: string;
    tax_rate?: number;
}

export interface ApplyGiftCardWrite {
    gift_card_number: string;
    gift_card_pin: string;
    reservation_key: string;
}

export interface AudioAssetRead {
    mux_playback_id: string;
    src: string | null;
}

export interface BulkPriceWrite {
    tables: number[];
    price: number;
}

export interface CSAreaRead {
    id: CSAreaId;
    name: string;
    inventory: CSInventoryBlockRead[][];
    duration: number;
    buffer_minutes: number;
    period_minutes: 5 | 10 | 15;
    advance_days: number;
    floor_plan_id: any | null;
    photo: string;
    crops: any | null;
    description: string | null;
    checkout_text: string | null;
    srcset: any;
    available_dates: IsoDate[];
}

export interface CSAreaWrite {
    id?: CSAreaId;
    name: string;
    inventory: CSInventoryBlockRead[][];
    duration: number;
    buffer_minutes?: number;
    period_minutes?: 5 | 10 | 15;
    advance_days?: number;
    floor_plan_id?: any | null;
    crops?: any | null;
    description?: string | null;
    checkout_text?: string | null;
}

export interface CSAvailabilityRead {
    start_time: string;
    available_guests: number;
}

export interface CSAvailabilityWrite {
    start_time: string;
    available_guests: number;
}

export interface CSDateListRead {
    id: CSDateId;
    date: IsoDate;
    inventory: CSInventoryBlockRead[];
    csarea_id: CSAreaId;
    duration: number | null;
    buffer_minutes: number | null;
}

export interface CSDateListWrite {
    id?: CSDateId;
    date: IsoDate;
    inventory: CSInventoryBlockWrite[];
    csarea_id: CSAreaId;
    duration?: number | null;
    buffer_minutes?: number | null;
}

export interface CSDateRead {
    id: CSDateId;
    date: IsoDate;
    inventory: CSInventoryBlockRead[];
    csarea: CSAreaRead;
    csarea_id: CSAreaId;
    duration: number | null;
    buffer_minutes: number | null;
    availability: any;
}

export interface CSDateReservationsRead {
    id: CSDateId;
    date: IsoDate;
    inventory: CSInventoryBlockRead[];
    csarea: CSAreaRead;
    csarea_id: CSAreaId;
    duration: number | null;
    buffer_minutes: number | null;
    availability: any;
    reservations: CSReservationRead[];
    tables: CSDateTableRead[];
}

export interface CSDateTableRead {
    id: CSDateTableId;
    name: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    held: boolean;
    shape: "circle" | "square" | "triangle" | "half-circle" | "piano";
    type_id: any;
}

export interface CSDateTableWrite {
    id?: CSDateTableId;
    name?: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w?: number;
    h?: number;
    rotation?: number;
    held?: boolean;
    shape?: "circle" | "square" | "triangle" | "half-circle" | "piano";
}

export interface CSDateWrite {
    id?: CSDateId;
    date: IsoDate;
    inventory: CSInventoryBlockWrite[];
    csarea_id: CSAreaId;
    duration?: number | null;
    buffer_minutes?: number | null;
    floor_plan_id?: any | null;
}

export interface CSInventoryBlockRead {
    start: string;
    end: string;
    totalSeats: number;
}

export interface CSInventoryBlockWrite {
    start: string;
    end: string;
    totalSeats: number;
}

export interface CSReservationCreateWrite {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    accepts_marketing?: boolean;
    csdate_id: number;
    start_time: string;
    status?: "unpaid" | "authorized" | "expected" | "arrived" | "seated" | "no show" | "canceled" | "cleared" | "failed" | "obsolete" | "paylater" | "partially_seated" | "table_ready";
    duration: number;
    number_of_guests?: number;
    guest_notes?: string;
    tables?: CSDateTableId[];
}

export interface CSReservationRead {
    id: CSReservationId;
    guest: GuestRead;
    guest_id: any;
    csdate_id: number;
    date: IsoDate;
    start_time: string;
    duration: number;
    number_of_guests: number;
    status: "unpaid" | "authorized" | "expected" | "arrived" | "seated" | "no show" | "canceled" | "cleared" | "failed" | "obsolete" | "paylater" | "partially_seated" | "table_ready";
    guest_notes: string;
    internal_notes: string;
    reservation_key: string;
    tables: number[];
    created: string;
    updated: string;
}

export interface CSReservationReserve {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    accepts_marketing?: boolean;
    start_time: string;
    number_of_guests?: number;
    guest_notes?: string;
}

export interface CSReservationWrite {
    id?: CSReservationId;
    start_time: string;
    duration: number;
    number_of_guests?: number;
    status?: "unpaid" | "authorized" | "expected" | "arrived" | "seated" | "no show" | "canceled" | "cleared" | "failed" | "obsolete" | "paylater" | "partially_seated" | "table_ready";
    guest_notes?: string;
    internal_notes?: string;
}

export interface CouponRead {
    id: CouponId;
    name: string;
    percent_off: number;
    max_redemptions: number;
    redeem_by: string;
    terminal_eligible: boolean;
}

export interface CouponWrite {
    id?: CouponId;
    name?: string;
    percent_off?: number;
    max_redemptions: number;
    redeem_by: string;
    terminal_eligible?: boolean;
}

export interface CropRead {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface CropWrite {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface FeaturesRead {
    continuous_service: boolean;
    performance: boolean;
    promo_codes: boolean;
    standing_room: boolean;
    enforce_minimums: boolean;
    fee_disclosure: boolean;
    terminal: boolean;
    skip_seating_time: boolean;
    livestream: boolean;
    residency_landing_v2: boolean;
}

export interface FloorPlanRead {
    id: FloorPlanId;
    name: string;
    tables: FloorPlanTableRead[];
    is_primary: boolean;
    room_layout: RoomLayoutRead;
    room_layout_id: RoomLayoutId | null;
}

export interface FloorPlanTableRead {
    id: TableId;
    name: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    held: boolean;
    default_price_per_person: number;
    shape: TableShapeType;
    type_id: TableTypeId;
    style: "performance" | "continuous";
    standing_room: boolean;
    accessibility_description: string | null;
}

export interface FloorPlanTableWrite {
    id?: TableId;
    name?: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w?: number;
    h?: number;
    rotation?: number;
    held?: boolean;
    default_price_per_person?: number;
    shape?: TableShapeType;
    type_id?: TableTypeId;
    style?: "performance" | "continuous";
    standing_room?: boolean;
    accessibility_description?: string | null;
}

export interface FloorPlanWrite {
    id?: FloorPlanId;
    name?: string;
    tables: FloorPlanTableWrite[];
    is_primary?: boolean;
    room_layout_id?: RoomLayoutId | null;
}

export interface GiftCardCheckRead {
    gift_card_number: string;
}

export interface GiftCardCheckWrite {
    gift_card_number: string;
}

export interface GuestDetailedRead {
    id: CustomerId;
    stripe_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    accepts_marketing: boolean;
    reservations: ReservationGuestDetailedRead[];
    tags: GuestTagId[];
}

export interface GuestRead {
    id: CustomerId;
    stripe_id: string;
    first_name: string;
    last_name: string;
    name: string;
    email: string;
    email_status: EmailStatusType;
    tags: GuestTagId[];
    phone: string;
    notes: string;
    total_lifetime_value: any;
    reservation_count: any;
}

export interface GuestSearchRead {
    id: CustomerId;
    stripe_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    accepts_marketing: boolean;
    transaction_count: number;
}

export interface GuestSegmentRead {
    id: GuestSegmentId;
    name: string;
    rules: GuestSegmentRules;
    sync_to_mailchimp: boolean;
    mailchimp_tag_name: string;
    sync_state: "off" | "pending" | "synced" | "failed";
    last_synced_at: string | null;
    member_count: number;
}

export interface GuestSegmentWrite {
    id?: GuestSegmentId;
    name?: string;
    rules: GuestSegmentRules;
    sync_to_mailchimp?: boolean;
}

export interface GuestTagRead {
    id: GuestTagId;
    name: string;
    icon_url: string;
}

export interface GuestWrite {
    id?: CustomerId;
    stripe_id?: string;
    first_name?: string;
    last_name?: string;
    email: string;
    tags?: GuestTagId[];
    phone?: string;
    notes?: string;
}

export interface ImageRead {
    src: string;
    size: string;
}

export interface InviteCodeRead {
    id: InviteCodeId;
    code: string;
    max_redemptions: number;
}

export interface InviteCodeWrite {
    id?: InviteCodeId;
    code: string;
    max_redemptions: number;
}

export interface MailchimpSettingsRead {
    connected: any;
    audience_id: string | null;
}

export interface MailchimpSettingsWrite {
    audience_id?: string | null;
}

export interface MassEmailWrite {
    subject: string;
    body: string;
    include_cancelled?: boolean;
}

export interface NotificationTemplateRead {
    notification_type: "reminder_email" | "thanks_email" | "confirm_email" | "cs_pending_email" | "cs_confirm_email" | "cs_reminder_email" | "cs_thanks_email";
    markdown_src: string | null;
    subject_src: string | null;
    footer_src: string | null;
}

export interface NotificationTemplateWrite {
    notification_type?: "reminder_email" | "thanks_email" | "confirm_email" | "cs_pending_email" | "cs_confirm_email" | "cs_reminder_email" | "cs_thanks_email";
    markdown_src?: string | null;
    subject_src?: string | null;
    footer_src?: string | null;
}

export interface PerformanceAddOnRead {
    id: PerformanceAddOnId;
    parent_id: AddOnId | null;
    is_active: boolean;
    name: string;
    description: string;
    tax_rate: number;
    price: number;
}

export interface PerformanceAddOnWrite {
    id?: PerformanceAddOnId;
    parent_id?: AddOnId | null;
    is_active?: boolean;
    name?: string;
    description: string;
    tax_rate?: number;
    price: number;
}

export interface PerformanceCreate {
    datetime: string;
    publish_datetime?: string | null;
    on_sale_datetime?: string | null;
    off_sale_datetime?: string | null;
    show_id: ShowId;
    arrangement_id: FloorPlanId;
    showtime_id: ShowTimeId;
    add_on_ids?: AddOnId[];
    residency_id?: ResidencyId | null;
    service_fee?: number | null;
    service_percentage?: number | null;
    button_text?: string;
    extra_template_text?: string;
    checkout_text?: string | null;
}

export interface PerformanceDetailedWrite {
    id?: PerformanceId;
    datetime: string;
    publish_datetime?: string | null;
    on_sale_datetime?: string | null;
    off_sale_datetime?: string | null;
    extra_template_text?: string;
    show_id?: ShowId;
    tables: PerformanceTableWrite[];
    add_ons: PerformanceAddOnWrite[];
    seating_times: PerformanceSeatingTimeWrite[];
    invite_codes?: InviteCodeWrite[];
    service_fee?: number | null;
    service_percentage?: number | null;
    button_text?: string | null;
    checkout_text?: string | null;
}

export interface PerformanceListRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string | null;
    on_sale_datetime: string | null;
    off_sale_datetime: string | null;
    created: string;
    show_id: any;
    floor_plan_id: any;
    show: ShowRead;
    sold: boolean;
    availability_state: AvailabilityState | null;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
    residencies: ResidencySimpleRead[];
}

export interface PerformanceRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string | null;
    on_sale_datetime: string | null;
    off_sale_datetime: string | null;
    created: string;
    show_id: ShowId;
    floor_plan_id: number;
    show: ShowRead;
    sold: boolean;
    availability_state: AvailabilityState | null;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
    tables: PerformanceTableRead[];
    add_ons: PerformanceAddOnRead[];
    seating_times: PerformanceSeatingTimeRead[];
    room_layout: RoomLayoutRead;
}

export interface PerformanceReportRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string | null;
    on_sale_datetime: string | null;
    off_sale_datetime: string | null;
    created: string;
    show_id: any;
    floor_plan_id: any;
    show: ShowRead;
    sold: boolean;
    availability_state: AvailabilityState | null;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
    booked_tables: number;
    table_count: number;
    booked_guests: number;
    amount_sum: number;
    add_on_amount_sum: number;
    tax_sum: number;
    service_fee_sum: number;
    discounted_sum: number;
    refunded_sum: number;
    net_face: number;
}

export interface PerformanceReservationsRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string;
    on_sale_datetime: string;
    off_sale_datetime: string | null;
    created: string;
    show_id: any;
    floor_plan_id: any;
    show: ShowRead;
    sold: boolean;
    availability_state: AvailabilityState | null;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
    room_layout: RoomLayoutRead;
    tables: PerformanceTableRead[];
    reservations: ReservationRead[];
    seating_times: PerformanceSeatingTimeRead[];
    add_ons: PerformanceAddOnRead[];
    invite_codes: InviteCodeRead[];
    extra_template_text: string;
}

export interface PerformanceSeatingTimeRead {
    id: PerformanceSeatingTimeId;
    time: string;
    maximum_guests: number;
    reserved_guests: number;
}

export interface PerformanceSeatingTimeWrite {
    id?: PerformanceSeatingTimeId;
    time: string;
    maximum_guests: number;
}

export interface PerformanceSettlementRead {
    financial_summary: SettlementFinancialSummaryRead;
    tiers: SettlementTierRead[];
    tier_totals: SettlementTierTotalsRead;
    comps_by_tier: SettlementCompTierRead[];
}

export interface PerformanceSimpleListRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string | null;
    on_sale_datetime: string | null;
    off_sale_datetime: string | null;
    created: string;
    show_id: any;
    floor_plan_id: any;
    show: ShowRead;
    sold: boolean;
    availability_state: AvailabilityState | null;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
}

export interface PerformanceTableRead {
    id: PerformanceTableId;
    name: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    held: boolean;
    price_per_person: number;
    shape: TableShapeType;
    performance: any;
    type_id: TableTypeId;
    standing_room: boolean;
    accessibility_description: string | null;
    available_guests: number;
    available: boolean;
}

export interface PerformanceTableWrite {
    id?: PerformanceTableId;
    name?: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w?: number;
    h?: number;
    rotation?: number;
    held?: boolean;
    price_per_person?: number;
    shape?: TableShapeType;
    performance: any;
    type_id?: TableTypeId;
    standing_room?: boolean;
    accessibility_description?: string | null;
}

export interface PerformanceWrite {
    id?: PerformanceId;
    datetime: string;
    publish_datetime?: string | null;
    on_sale_datetime?: string | null;
    off_sale_datetime?: string | null;
    show_id?: ShowId;
    service_fee?: number | null;
    service_percentage?: number | null;
    button_text?: string | null;
    checkout_text?: string | null;
    seating_times?: PerformanceSeatingTimeWrite[];
}

export interface PromotionCodeRead {
    id: PromotionCodeId;
    name: string;
    coupon: CouponRead;
    code: string;
    active: boolean;
    expires_at: string;
    max_redemptions: number;
    redemption_count: number;
    performance_id: PerformanceId | null;
}

export interface PromotionCodeWrite {
    id?: PromotionCodeId;
    name?: string;
    coupon: CouponWrite;
    code: string;
    active: boolean;
    expires_at: string;
    max_redemptions: number;
    performance_id?: PerformanceId | null;
}

export interface RefundDetailedWrite {
    amount: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    refund_note?: string;
}

export interface ReportInstanceRead {
    id: ReportInstanceId;
    created: string;
    status: ReportStatusType;
    name: string;
    report: "guest" | "transaction" | "reservation" | "performance" | "sales" | "tt_presents";
    args: ReportArgs;
    user_id: any;
}

export interface ReportInstanceWrite {
    id?: ReportInstanceId;
    name?: string;
    report?: "guest" | "transaction" | "reservation" | "performance" | "sales" | "tt_presents";
    args?: ReportArgs;
}

export interface ReportScheduleRead {
    id: ReportScheduleId;
    name: string;
    report_type: "guest" | "transaction" | "reservation" | "performance" | "sales" | "tt_presents";
    recipients: string[];
    frequency: "daily" | "weekly" | "monthly";
    args: ReportArgs;
    created: string;
    updated: string;
}

export interface ReportScheduleWrite {
    id?: ReportScheduleId;
    name?: string;
    report_type?: "guest" | "transaction" | "reservation" | "performance" | "sales" | "tt_presents";
    recipients?: string[];
    frequency?: "daily" | "weekly" | "monthly";
    args?: ReportArgs;
}

export interface ReservationAddOnRead {
    performance_add_on_id: PerformanceAddOnId;
    quantity: number;
    price: number;
}

export interface ReservationAddOnWrite {
    performance_add_on_id: PerformanceAddOnId;
    quantity: number;
}

export interface ReservationCreate {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    accepts_marketing?: boolean;
    amount?: string;
    add_on_amount?: string;
    service_fee?: string;
    tax?: string;
    discounted?: string;
    number_of_guests: number;
    promo_code?: string;
    coupon_id?: CouponId;
    tables: PerformanceTableId[];
    add_ons?: ReservationAddOnWrite[];
    performance: PerformanceId;
    seating_time?: PerformanceSeatingTimeId;
    invite_code?: string | null;
    guest_notes?: string;
    internal_notes?: string;
    status?: "unpaid" | "authorized" | "expected" | "arrived" | "seated" | "no show" | "canceled" | "cleared" | "failed" | "obsolete" | "paylater" | "partially_seated" | "table_ready";
    reader_id?: StripeReaderId;
    tags?: ReservationTagId[];
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
}

export interface ReservationDetailedRead {
    id: ReservationId;
    created: string;
    stripe_total: any;
    user_id: UserId;
    contact: GuestRead;
    seating_time_id: number;
    performance: PerformanceListRead;
    performance_id: PerformanceId;
    transactions: TransactionDetailedRead[];
    tables: PerformanceTableId[];
    res_tables: ReservationTableDetailedRead[];
    number_of_guests: number;
    guest_notes: string;
    internal_notes: string;
    amount: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    refunded: number | null;
    discounted: number;
    status: ReservationStatusType;
    failure_reason: string | null;
    promotion_code: PromotionCodeRead;
    invite_code_code: string;
    add_ons: ReservationAddOnRead[];
    tags: ReservationTagId[];
    expiration: string;
    payment: string | null;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
}

export interface ReservationGuestDetailedRead {
    id: ReservationId;
    user_id: UserId;
    seating_time_id: PerformanceSeatingTimeId;
    performance: PerformanceListRead;
    performance_id: PerformanceId;
    transactions: TransactionDetailedRead[];
    tables: PerformanceTableId[];
    number_of_guests: number;
    guest_notes: string;
    internal_notes: string;
    amount: number;
    tax: number;
    status: ReservationStatusType;
    service_fee: number;
}

export interface ReservationGuestModify {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    accepts_marketing?: boolean;
    amount?: string;
    add_on_amount?: string;
    service_fee?: string;
    tax?: string;
    discounted?: string;
    number_of_guests: number;
    promo_code?: string;
    coupon_id?: CouponId;
    tables: any[];
    add_ons?: ReservationAddOnWrite[];
    reservation_key: string;
    seating_time?: PerformanceSeatingTimeId;
    performance_id?: PerformanceId;
    invite_code?: string | null;
    status?: "unpaid";
}

export interface ReservationGuestRead {
    id: CustomerId;
    stripe_id: string;
    first_name: string;
    last_name: string;
    name: string;
    email: string;
    email_status: string;
    phone: string;
    notes: string;
}

export interface ReservationGuestWrite {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    accepts_marketing?: boolean;
}

export interface ReservationListRead {
    id: ReservationId;
    created: string;
    status: ReservationStatusType;
    contact: ReservationGuestRead;
    performance_id: PerformanceId;
    performance: ReservationPerformanceSimpleListRead;
    amount: number;
    tax: number;
    service_fee: number;
    add_on_amount: number;
    number_of_guests: number;
}

export interface ReservationPaidUpdate {
    id?: ReservationId;
    status?: ReservationStatusType;
    seating_time: any;
    performance_id?: PerformanceId;
    tables: any[];
    tags?: ReservationTagId[];
    guest?: GuestWrite;
    number_of_guests: number;
    guest_notes?: string;
    internal_notes?: string;
    add_ons?: ReservationAddOnWrite[];
    reader_id: StripeReaderId;
}

export interface ReservationPerformanceSimpleListRead {
    id: PerformanceId;
    datetime: string;
    publish_datetime: string | null;
    on_sale_datetime: string | null;
    off_sale_datetime: string | null;
    created: string;
    show_id: any;
    floor_plan_id: any;
    show: ShowRead;
    service_fee: number | null;
    service_percentage: number | null;
    button_text: string | null;
    checkout_text: string | null;
}

export interface ReservationRead {
    id: ReservationId;
    created: string;
    user: any | null;
    user_id: UserId;
    contact: GuestRead;
    seating_time_id: PerformanceSeatingTimeId;
    seating_time: any;
    performance_id: PerformanceId;
    performance: any;
    tables: PerformanceTableId[];
    number_of_guests: number;
    fee: number;
    guest_notes: string;
    internal_notes: string;
    amount: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    refunded: number | null;
    discounted: number;
    status: ReservationStatusType;
    promotion_code: PromotionCodeRead;
    add_ons: ReservationAddOnRead[];
    tags: ReservationTagId[];
    expiration: string;
    coupon: CouponRead;
    payment: string | null;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
}

export interface ReservationTableDetailedRead {
    performancetable_id: any;
    price_per_person: number | null;
    guests: number | null;
}

export interface ReservationTagRead {
    id: ReservationTagId;
    name: string;
    icon_url: string;
}

export interface ReservationUpdate {
    id?: ReservationId;
    status?: ReservationStatusType;
    seating_time: any;
    performance_id?: PerformanceId;
    tables: any[];
    tags?: ReservationTagId[];
    guest?: GuestWrite;
    number_of_guests: number;
    guest_notes?: string;
    internal_notes?: string;
    add_ons?: ReservationAddOnWrite[];
}

export interface ResidencyExternalLinkRead {
    id: ResidencyExternalLinkId;
    label: string;
    icon: string;
    url: string;
    position: number;
}

export interface ResidencyExternalLinkWrite {
    id?: ResidencyExternalLinkId;
    label?: string;
    icon?: string;
    url: string;
    position?: number;
}

export interface ResidencyListRead {
    id: ResidencyId;
    name: string;
    description: string;
    slug: string;
    show_id: number;
    show: ShowRead;
    start_date: IsoDate;
    end_date: IsoDate;
    performance_count: number;
}

export interface ResidencyPublicRead {
    id: ResidencyId;
    name: string;
    description: string;
    slug: string;
    start_date: IsoDate;
    end_date: IsoDate;
    performances: PerformanceSimpleListRead[];
    performance_ids: PerformanceId[];
    show_id: ShowId;
    show: ShowRead;
    video_url: string;
    tagline: string;
    intro: string;
    social_proof: string;
    external_links: ResidencyExternalLinkRead[];
    audio: AudioAssetRead | null;
    video: VideoAssetRead | null;
}

export interface ResidencyRead {
    id: ResidencyId;
    name: string;
    description: string;
    slug: string;
    start_date: IsoDate;
    end_date: IsoDate;
    performances: PerformanceSimpleListRead[];
    performance_ids: PerformanceId[];
    show_id: ShowId;
    show: ShowRead;
    video_url: string;
    tagline: string;
    intro: string;
    social_proof: string;
    external_links: ResidencyExternalLinkRead[];
    audio: AudioAssetRead | null;
    video: VideoAssetRead | null;
    invite_codes: InviteCodeRead[];
}

export interface ResidencySimpleRead {
    id: ResidencyId;
    name: string;
    slug: string;
}

export interface ResidencySimpleWrite {
    id?: ResidencyId;
    name?: string;
    slug: string;
}

export interface ResidencyWrite {
    id?: ResidencyId;
    name?: string;
    description?: string;
    performance_ids: PerformanceId[];
    show_id?: ShowId;
    video_url?: string;
    tagline?: string;
    intro?: string;
    social_proof?: string;
    external_links?: ResidencyExternalLinkWrite[];
    invite_codes?: InviteCodeWrite[];
}

export interface RoomLayoutRead {
    id: RoomLayoutId;
    name: string;
    aspect_ratio: number;
    accessibility_description: string | null;
    background_url: string;
    dark_background_url: string;
    svg_height: number;
}

export interface RoomLayoutWrite {
    id?: RoomLayoutId;
    name?: string;
    aspect_ratio?: number;
    accessibility_description?: string | null;
}

export interface SearchGuestRead {
    id: CustomerId;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    transaction_count: number;
    recent_reservations: any;
}

export interface SearchResidencyRead {
    id: ResidencyId;
    name: string;
    slug: string;
    description: string;
    performance_count: number;
}

export interface SearchShowRead {
    id: ShowId;
    name: string;
    description: string;
    image: string;
    upcoming_performances: any;
}

export interface SeatingTimeRead {
    id: SeatingTimeId;
    time: string;
    maximum_guests: number;
    reserved_guests: number;
}

export interface SeatingTimeWrite {
    id?: SeatingTimeId;
    time: string;
    maximum_guests: number;
}

export interface SettingRead {
    status: "setup" | "active" | "demo" | "inactv";
    background_path: any;
    dark_background_path: any;
    checkout_confirmation_text: string;
    checkout_text: string;
    company_id: any;
    features: FeaturesRead;
    guest_tags: GuestTagRead[];
    link_url: string;
    logo_url: string;
    css_url: string;
    max_guests: number;
    payment_timeout: number;
    public_key: any;
    ratio: number;
    reply_to: string;
    reservation_tags: ReservationTagRead[];
    seating_time_text: string;
    table_selection_text: string;
    about_venue: string;
    service_fee: number;
    service_percentage: number;
    stripe_account: string;
    stripe_charges_enabled: boolean;
    table_types: TableTypeRead[];
    tax_rate: number;
    fee_tax_rate: number;
    terminal_service_fee: number;
    terminal_service_percentage: number;
    application_fee: number;
    application_fee_per_reservation: number;
    timezone: any;
    venue_currency: string;
    venue_locale: string;
    venue_id: any;
    venue_name: string;
    venue_slug: string;
    display_timer: boolean;
    google_pixel_id: string;
    facebook_pixel_id: string;
    gtm_container_id: string;
    image_ratio_desktop: "square" | "rectangle";
    image_ratio_mobile: "square" | "rectangle";
    guest_html: any;
}

export interface SettingWrite {
    service_fee?: number;
    service_percentage?: number;
    terminal_service_fee?: number;
    terminal_service_percentage?: number;
    reply_to?: string;
    link_url?: string;
    tax_rate?: number;
    fee_tax_rate?: number;
    timezone?: string;
    max_guests?: number;
    payment_timeout?: number;
    display_timer?: boolean;
    residency_landing_v2_enabled?: boolean;
    checkout_text?: string;
    checkout_confirmation_text?: string;
    seating_time_text?: string;
    table_selection_text?: string;
    about_venue?: string;
    google_pixel_id?: string;
    facebook_pixel_id?: string;
    gtm_container_id?: string;
    image_ratio_desktop?: "square" | "rectangle";
    image_ratio_mobile?: "square" | "rectangle";
}

export interface SettlementCompTierRead {
    price: number;
    table_count: number;
    guests: number;
}

export interface SettlementFinancialSummaryRead {
    ticket_gross: number;
    refunded: number;
    discounted: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    comps: number;
    net: number;
    comp_count: number;
    comp_guests: number;
}

export interface SettlementTierRead {
    price: number;
    advance_count: number;
    walk_in_count: number;
    revenue: number;
}

export interface SettlementTierTotalsRead {
    advance_count: number;
    walk_in_count: number;
    revenue: number;
}

export interface ShowCropRead {
    square: CropRead;
    rectangle: CropRead;
}

export interface ShowCropWrite {
    square: CropWrite;
    rectangle: CropWrite;
}

export interface ShowDetailedRead {
    id: ShowId;
    name: string;
    image: string;
    crops: any | null;
    srcset: ShowSrcsetRead;
    price_per_person: number[] | null;
    position: number[];
    description: string;
}

export interface ShowDetailedWrite {
    id?: ShowId;
    name?: string;
    crops?: any | null;
    price_per_person?: number[] | null;
    position?: number[];
    description?: string;
}

export interface ShowRead {
    id: ShowId;
    name: string;
    image: string;
    srcset: ShowSrcsetRead;
    price_per_person: number[] | null;
    position: number[];
    description: string;
    audio: AudioAssetRead | null;
    video: VideoAssetRead | null;
}

export interface ShowReportRead {
    id: ShowId;
    name: string;
    image: string;
    srcset: ShowSrcsetRead;
    is_recurring: boolean;
    performance_count: number;
    total_reservations: number;
    total_guests: number;
    total_revenue: number;
    avg_guests_per_show: number;
    avg_ticket_price: number;
    tickets_per_performance: number;
    show_frequency: number;
    revenue_per_performance: number;
    advance_booking_days: number;
    table_sell_through_pct: number;
    tickets_per_day_on_sale: number;
    day_of_week_breakdown: any;
}

export interface ShowSrcsetRead {
    sqSm: ImageRead;
    sqLg: ImageRead;
    sqXl: ImageRead;
    rectSm: ImageRead;
    rectLg: ImageRead;
    rectXl: ImageRead;
    original: ImageRead;
}

export interface ShowTimeRead {
    id: ShowTimeId;
    name: string;
    time: string;
    seating_times: ShowTimeSeatingTimeRead[];
}

export interface ShowTimeSeatingTimeRead {
    time: string;
    maximum_guests: number;
}

export interface ShowTimeSeatingTimeWrite {
    time: string;
    maximum_guests: number;
}

export interface ShowTimeWrite {
    id?: ShowTimeId;
    name?: string;
    time: string;
    seating_times?: ShowTimeSeatingTimeWrite[];
}

export interface ShowWithPerformancesRead {
    id: ShowId;
    name: string;
    image: string;
    crops: any | null;
    srcset: ShowSrcsetRead;
    price_per_person: number[] | null;
    position: number[];
    description: string;
    performances: any;
}

export interface ShowWithPerformancesWrite {
    id?: ShowId;
    name?: string;
    crops?: any | null;
    price_per_person?: number[] | null;
    position?: number[];
    description?: string;
}

export interface ShowWrite {
    id?: ShowId;
    name?: string;
    price_per_person?: number[] | null;
    position?: number[];
    description?: string;
}

export interface StripeLocationRead {
    id: StripeLocationId;
    display_name: string;
}

export interface StripeReaderActionRead {
    failure_code: string;
    failure_message: string;
    payment_intent_id: string;
    status: StripeReaderOutcome;
    type: string;
}

export interface StripeReaderMessageRead {
    status: StripeReaderOutcome;
    reader_id: StripeReaderId;
    failure_code: string;
    failure_message: string;
    payment_intent_id: string;
}

export interface StripeReaderRead {
    id: StripeReaderId;
    label: string;
    action: StripeReaderActionRead;
    location_id: StripeLocationId;
    status: StripeReaderStatus;
    device_type: string;
}

export interface SummaryReportParameters {
    start: IsoDate;
    end: IsoDate;
}

export interface SummaryReportRead {
    face: number;
    add_ons: number;
    discounts: number;
    refunds: number;
    service_fee: number;
    ticket_tax: number;
    add_ons_tax: number;
    stripe_fees: number;
    application_fees: number;
    guests_sum: number;
}

export interface TableRead {
    id: TableId;
    name: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    held: boolean;
    default_price_per_person: number;
    shape: TableShapeType;
    style: "performance" | "continuous";
    type_id: TableTypeId;
    standing_room: boolean;
    accessibility_description: string | null;
    arrangement: FloorPlanId;
}

export interface TableTypeRead {
    id: TableTypeId;
    name: string;
    abbreviation: string;
}

export interface TableTypeWrite {
    id?: TableTypeId;
    name?: string;
    abbreviation?: string;
}

export interface TableWrite {
    id?: TableId;
    name?: string;
    minimum_guests: number;
    maximum_guests: number;
    x: number;
    y: number;
    w?: number;
    h?: number;
    rotation?: number;
    held?: boolean;
    default_price_per_person?: number;
    shape?: TableShapeType;
    style?: "performance" | "continuous";
    type_id?: TableTypeId;
    standing_room?: boolean;
    accessibility_description?: string | null;
    arrangement: FloorPlanId;
}

export interface TransactionDetailedRead {
    id: TransactionId;
    created: string;
    user_id: any;
    reservation_id: any;
    client_secret: string;
    payment_intent_id: any;
    number_of_guests: number | null;
    amount: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    discounted: number;
    refunded: number | null;
    paid: boolean;
    authorized: string | null;
    captured: string | null;
    authorization_expires_at: string | null;
    capture_method: string;
    application_fee: number | null;
    stripe_fee: number | null;
    payment_provider: string | null;
    payment_method_type: string | null;
    payment_brand: string | null;
    tables: any;
}

export interface TransactionRead {
    id: TransactionId;
    user: any | null;
    customer: any | null;
    reservation: any;
    timezone: number;
    client_secret: string;
    amount: number;
    add_on_amount: number;
    tax: number;
    service_fee: number;
    refunded: number | null;
    discounted: number;
    paid: boolean;
    authorized: string | null;
    captured: string | null;
    authorization_expires_at: string | null;
    capture_method: string;
    application_fee: number | null;
    stripe_fee: number | null;
    payment_provider: string | null;
    payment_method_type: string | null;
    payment_brand: string | null;
}

export interface TransactionWrite {
    id?: TransactionId;
    user?: any | null;
    customer?: any | null;
    reservation: any;
    timezone?: number;
    client_secret?: string;
    amount?: number;
    add_on_amount?: number;
    tax?: number;
    service_fee?: number;
    refunded?: number | null;
    discounted?: number;
    paid?: boolean;
    authorized?: string | null;
    captured?: string | null;
    authorization_expires_at?: string | null;
    capture_method?: string;
    application_fee?: number | null;
    stripe_fee?: number | null;
    payment_provider?: string | null;
    payment_method_type?: string | null;
    payment_brand?: string | null;
}

export interface UserRead {
    id: UserId;
    email: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    accepts_marketing: boolean;
    venues: UserVenueRead[];
    hubspot_token: any;
    groups: UserGroupType[];
    last_login: string | null;
    is_active: boolean;
    intercom_jwt: any;
}

export interface UserVenueRead {
    name: string;
    primary_site: string;
}

export interface UserWrite {
    id?: UserId;
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string | null;
    accepts_marketing?: boolean;
    groups: UserGroupType[];
    is_active?: boolean;
}

export interface VideoAssetRead {
    mux_playback_id: string;
    src: string | null;
    poster: string | null;
    status: "pending" | "processing" | "ready" | "errored";
    duration_ms: number | null;
    aspect_ratio: string;
}

